import { CacheControl } from '@tusbar/cache-control'
import { GraphQLFormattedError } from 'graphql/error'

import { ApolloClientProfile } from '@syconium/weeping-figs'

import { createCanonicalReviewsPageUrl } from '../../../../../app/_components/navigation/createReviewsPageUrl'
import { NotFoundPage } from '../../../../../app/_components/pages/NotFound.client'
import { SeoMeta } from '../../../../../components/SeoMeta'
import {
  primeClientForReviewsProductGroupData,
  useReviewsProductGroupData,
} from '../../../../../containers/ReviewsProductGroupDataService/useReviewsProductGroupData'
import { cachePolicies } from '../../../../../lib/response-cache'
import { singleQueryParamValue } from '../../../../../lib/utils'
import { MagnoliaPage } from '../../../MagnoliaApp'

import { ReviewsPageView } from './ReviewsPageView'

export const ReviewsPage: MagnoliaPage<{
  handle: string
  writeReview: boolean
  showNotFound: boolean
  color: string | undefined | null
}> = ({ writeReview, handle, showNotFound, color: ssrColor }) => {
  const clientSearchParams =
    typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : undefined
  const color = clientSearchParams ? clientSearchParams.get('color') ?? undefined : ssrColor

  const { data } = useReviewsProductGroupData({
    handle,
    colorKey: color,
  })

  if (showNotFound) return <NotFoundPage />

  const seoTitle = data?.seoTitle
  const canonicalUrl = data?.productGroupHandle
    ? createCanonicalReviewsPageUrl({
        handle: data.productGroupHandle,
      })
    : undefined

  return (
    <>
      {canonicalUrl ? <SeoMeta title={seoTitle} canonicalUrl={canonicalUrl} /> : null}
      <ReviewsPageView handle={handle} colorKey={color} writeReview={writeReview} />
    </>
  )
}

ReviewsPage.getInitialProps = async function (pageContext) {
  const color = singleQueryParamValue(pageContext.query.color, undefined)
  const handle: string = singleQueryParamValue(pageContext.query.handle, '')
  const writeReview: string = singleQueryParamValue(pageContext.query['write-review'], '')

  /**
   * Prime the Apollo Client cache here since it runs during SSR and during the period where
   * the client is faded out to signify to the user we are retrieving the next page.
   *
   * Then when we are actually rendering the next page the hook that this query comes from
   * will be able to return its results instantly since it was primed.
   */
  const client = pageContext.apolloClientFactory.for(ApolloClientProfile.FigsPublicSupergraph)
  const { data: _data, errors } = await primeClientForReviewsProductGroupData({
    handle,
    client,
    colorKey: color,
  })

  const notFoundError = (errors ?? []).find(
    graphQLError => graphQLError.extensions?.errorType === 'NOT_FOUND'
  )
  if (pageContext.res && notFoundError) {
    pageContext.res.statusCode = 404
  } else if (pageContext.res && errors && errors.length > 0) {
    pageContext.res.statusCode = 500
  }

  return {
    handle,
    responseCacheControl: cachePolicy(errors),
    writeReview: writeReview === 'true',
    showNotFound: pageContext.res?.statusCode === 404 || pageContext.res?.statusCode === 500,
    color,
  }
}

function cachePolicy(errors: ReadonlyArray<GraphQLFormattedError> | undefined): CacheControl {
  return errors && errors.length > 0 ? cachePolicies.noCache : cachePolicies.publicHtmlPolicy
}
